'use client'

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { useController, useFormContext } from 'react-hook-form'

import type {
  Question,
  QuestionnairePage,
} from '@/lib/questionnaires/Questionnaire'
import {
  PrescriptionQuizFooter,
  QuizFieldError,
  prescriptionQuestionItemMotion,
  prescriptionQuestionListMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'

type QuestionnaireQuestions = Exclude<
  QuestionnairePage,
  'upload-supporting-document' | 'personal-information' | 'call-arrangement'
>

const QUESTION_LABELS: Record<string, string> = {
  condition: 'What condition are you taking this for?',
  isPrescribedByRegularGP: 'Was this medicine prescribed by your regular GP?',
  isSameDose: 'Are your symptoms well controlled with the current dose?',
  hasSideEffects: 'Have you had any side effects from this medicine?',
  isOrTryingToBePregnant: 'Are you pregnant or trying to become pregnant?',
  isBreastFeeding: 'Are you breastfeeding?',
  hasSignificantMedicalCondition:
    'Do you have any significant current or past medical conditions?',
  significantMedicalConditions: 'Tell us about those medical conditions.',
  isOnOtherMedications: 'Are you taking any other medicines?',
  otherMedications: 'List the other medicines you take.',
  hasAllergy: 'Do you have any allergies?',
  allergies: 'Tell us about your allergies.',
  followUpAgreement:
    'Will you see your regular GP for a follow-up in the next 1–3 months?',
  inAustraliaAgreement: 'Are you in Australia right now?',
  allowsAutoNoteTakingSoftware:
    'Can your doctor use secure note-taking software during the consultation?',
}

export function PrescriptionQuestionnaireFields({
  currentStep,
  firstPage,
  busy,
  onBack,
}: {
  currentStep: QuestionnaireQuestions
  firstPage: boolean
  busy: boolean
  onBack: () => void
}) {
  return (
    <>
      <motion.div
        animate="animate"
        className="flex flex-col gap-7 sm:gap-8"
        initial="initial"
        variants={prescriptionQuestionListMotion}
      >
        <AnimatePresence initial>
          {currentStep.questions.map((question) => (
            <motion.div
              key={questionKey(question)}
              layout="position"
              variants={prescriptionQuestionItemMotion}
            >
              <PrescriptionQuestionField question={question} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <PrescriptionQuizFooter
        backHidden={firstPage}
        nextLoading={busy}
        submit
        onBack={onBack}
      />
    </>
  )
}

function PrescriptionQuestionField({ question }: { question: Question }) {
  switch (question.type) {
    case 'free-text':
      return (
        <QuizTextArea
          name={question.key}
          label={QUESTION_LABELS[question.key] || question.title}
          placeholder={
            question.key === 'condition'
              ? 'For example: high blood pressure, depression or diabetes'
              : 'Add details for the doctor'
          }
        />
      )
    case 'yes-no':
      return (
        <QuizChoiceQuestion
          name={question.key}
          label={QUESTION_LABELS[question.key] || question.title}
          options={[
            { id: 'Yes', label: 'Yes' },
            { id: 'No', label: 'No' },
          ]}
        />
      )
    case 'single-choice':
      return (
        <QuizChoiceQuestion
          name={question.key}
          label={QUESTION_LABELS[question.key] || question.title}
          options={question.options}
        />
      )
    case 'duration':
      return (
        <QuizDurationQuestion
          label="How long have you been taking it?"
          monthName={question.monthKey}
          yearName={question.yearKey}
        />
      )
    default:
      return null
  }
}

function QuizTextArea({
  name,
  label,
  placeholder,
}: {
  name: string
  label: string
  placeholder?: string
}) {
  const { field, fieldState } = useController({ name })
  const showError = fieldState.isTouched && Boolean(fieldState.error)

  return (
    <fieldset>
      <label
        className="mb-2 block text-[15px] font-semibold leading-[21px] sm:text-[15.5px] sm:leading-[22px]"
        htmlFor={name}
      >
        {label}
      </label>
      <textarea
        {...field}
        className={clsx(
          'min-h-[110px] w-full resize-y rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 py-3.5 text-[15px] leading-5 text-[#0B0B0F] outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F55] focus:shadow-[0_0_0_3px_rgba(11,11,15,0.05)]',
          showError && 'border-[#C6453C] focus:border-[#C6453C]',
        )}
        id={name}
        placeholder={placeholder}
        rows={4}
        value={field.value ?? ''}
      />
      {showError && (
        <QuizFieldError>{fieldState.error?.message}</QuizFieldError>
      )}
    </fieldset>
  )
}

function QuizChoiceQuestion({
  name,
  label,
  options,
}: {
  name: string
  label: string
  options: Array<{ id: string; label: string }>
}) {
  const { field, fieldState } = useController({ name })
  const { clearErrors, setValue } = useFormContext()
  const showError =
    (fieldState.isTouched || Boolean(field.value)) && Boolean(fieldState.error)

  return (
    <fieldset>
      <legend className="mb-3 text-[15px] font-semibold leading-[21px] sm:text-[15.5px] sm:leading-[22px]">
        {label}
      </legend>
      <div
        className={clsx(
          'grid gap-2.5 sm:gap-3',
          options.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2',
        )}
      >
        {options.map((option) => {
          const selected = String(field.value) === String(option.id)
          const selectedError = selected && showError

          return (
            <label
              className={clsx(
                'flex min-h-[55px] cursor-pointer items-center rounded-[16px] border-[1.5px] bg-[#F4F4F6] px-4 text-[15px] font-normal leading-5 transition-[border-color,color,background-color] duration-300',
                selectedError
                  ? 'border-[#C6453C] text-[#0B0B0F] hover:border-[#C6453C] hover:bg-[#EFEFF2]'
                  : selected
                    ? 'border-[#0B0B0F] text-[#0B0B0F] hover:bg-[#EFEFF2]'
                    : 'border-transparent text-[#5C5C64] hover:bg-[#EFEFF2]',
              )}
              key={option.id}
            >
              <input
                checked={selected}
                className="sr-only"
                name={field.name}
                onBlur={field.onBlur}
                onChange={() => {
                  // The previous answer's error must not flash while this one validates.
                  clearErrors(name)
                  setValue(name, option.id, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  })
                }}
                type="radio"
                value={option.id}
              />
              <span>{option.label}</span>
              <span
                aria-hidden="true"
                className={clsx(
                  'ml-auto flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-[border-color,background-color] duration-300',
                  selectedError
                    ? 'border-[#C6453C] bg-[#C6453C]'
                    : selected
                      ? 'border-[#0B0B0F] bg-[#0B0B0F]'
                      : 'border-[#C4C4CA] bg-white',
                )}
              >
                {selected && <span className="h-2 w-2 rounded-full bg-white" />}
              </span>
            </label>
          )
        })}
      </div>
      {showError && (
        <QuizFieldError>{fieldState.error?.message}</QuizFieldError>
      )}
    </fieldset>
  )
}

function QuizDurationQuestion({
  label,
  monthName,
  yearName,
}: {
  label: string
  monthName: string
  yearName: string
}) {
  const { register, formState } = useFormContext()
  const yearError = formState.errors[yearName]
  const monthError = formState.errors[monthName]
  const yearTouched = formState.touchedFields[yearName]
  const monthTouched = formState.touchedFields[monthName]
  const showError = Boolean(
    (yearTouched || monthTouched) && (yearError || monthError),
  )
  const error = (monthError || yearError) as { message?: string } | undefined

  return (
    <fieldset>
      <legend className="mb-3 text-[15px] font-semibold leading-[21px] sm:text-[15.5px] sm:leading-[22px]">
        {label}
      </legend>
      <div className="grid grid-cols-2 gap-3">
        <label className="relative">
          <span className="sr-only">Years</span>
          <input
            {...register(yearName, { deps: monthName })}
            className={clsx(
              'h-[55px] w-full rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 pr-16 text-[15px] leading-5 outline-none transition-colors duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F55]',
              showError && 'border-[#C6453C]',
            )}
            inputMode="numeric"
            placeholder="0"
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[13px] text-[#82828B]">
            years
          </span>
        </label>
        <label className="relative">
          <span className="sr-only">Months</span>
          <input
            {...register(monthName)}
            className={clsx(
              'h-[55px] w-full rounded-[14px] border-[1.5px] border-transparent bg-[#F4F4F6] px-4 pr-20 text-[15px] leading-5 outline-none transition-colors duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F55]',
              showError && 'border-[#C6453C]',
            )}
            inputMode="numeric"
            placeholder="0"
          />
          <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[13px] text-[#82828B]">
            months
          </span>
        </label>
      </div>
      {showError && <QuizFieldError>{error?.message}</QuizFieldError>}
    </fieldset>
  )
}

function questionKey(question: Question) {
  switch (question.type) {
    case 'duration':
      return `${question.yearKey}-${question.monthKey}`
    case 'date-range':
      return `${question.startKey}-${question.endKey}`
    default:
      return question.key
  }
}
